import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ChefHat } from 'lucide-react';

export const ChowMein: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Classic Chow Mein Recipe | Chinese Food Maps</title>
        <meta name="description" content="Learn how to make authentic Chinese Chow Mein at home. Our recipe shows you how to create the perfect stir-fried noodles with crispy vegetables and tender meat." />
        <meta property="og:title" content="Classic Chow Mein Recipe | Chinese Food Maps" />
        <meta property="og:description" content="Learn how to make authentic Chinese Chow Mein at home." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://chinesefoodmaps.com/recipes/chow-mein" />
        <meta property="og:image" content="https://cdn-icons-png.flaticon.com/512/2548/2548242.png" />
        <link rel="canonical" href="https://chinesefoodmaps.com/recipes/chow-mein" />
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-red-900">Classic Chow Mein Recipe</h1>
        
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="prose max-w-none text-gray-600">
            <p className="text-lg leading-relaxed mb-6">
              Chow Mein (炒面) literally means "stir-fried noodles" and is one of the most popular Chinese dishes worldwide. This recipe will teach you how to make authentic restaurant-style chow mein at home.
            </p>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Ingredients:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>1 lb fresh chow mein noodles</li>
              <li>½ lb chicken, sliced (or protein of choice)</li>
              <li>2 cups cabbage, shredded</li>
              <li>1 carrot, julienned</li>
              <li>2 celery stalks, sliced</li>
              <li>1 onion, sliced</li>
              <li>2 cups bean sprouts</li>
              <li>2 green onions, chopped</li>
              <li>3 cloves garlic, minced</li>
              <li>2 tbsp vegetable oil</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">For the Sauce:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>2 tbsp oyster sauce</li>
              <li>1 tbsp soy sauce</li>
              <li>1 tsp dark soy sauce</li>
              <li>½ tsp sesame oil</li>
              <li>¼ tsp white pepper</li>
              <li>2 tbsp water</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Instructions:</h3>
            <ol className="list-decimal pl-6 mb-6 space-y-4">
              <li>
                <strong>Prepare Noodles:</strong> Cook noodles according to package instructions. Drain and set aside.
              </li>
              <li>
                <strong>Cook Protein:</strong> Stir-fry chicken until golden brown. Remove and set aside.
              </li>
              <li>
                <strong>Cook Vegetables:</strong> Stir-fry onions, carrots, and celery until softened. Add cabbage.
              </li>
              <li>
                <strong>Combine:</strong> Add noodles, sauce, and chicken. Toss well.
              </li>
              <li>
                <strong>Finish:</strong> Add bean sprouts and green onions. Stir-fry until heated through.
              </li>
            </ol>

            <div className="bg-yellow-50 rounded-lg p-6 mt-8">
              <h4 className="text-lg font-semibold text-red-800 mb-2">Pro Tips:</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use high heat throughout cooking</li>
                <li>Don't overcook the noodles - they should be slightly firm</li>
                <li>Cut all vegetables to similar sizes for even cooking</li>
                <li>Add bean sprouts last to keep them crispy</li>
                <li>Serve immediately while hot and noodles are still crispy</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};